/**
 * P_USER_WELCOME_DISPATCH_2.1 (2026-06-29) — shared timezone helpers.
 *
 * Background
 * ----------
 * The backend stores all engagement / delivery / audit timestamps as
 * UTC ISO 8601 strings (e.g. `"2026-06-29T16:00:00.123456+00:00"` —
 * see `backend/services/engagement.py`). Until 2026-06-29 many
 * frontend pages extracted display strings via naïve substring
 * slicing:
 *
 *     iso.slice(11, 16)                       // "16:00"
 *     iso.replace('T', ' ').slice(0, 19)      // "2026-06-29 16:00:00"
 *
 * That rendered UTC clock values as if they were already in the
 * operator's local timezone, so a dispatch opened at 12:00 PM EDT
 * (16:00 UTC) was displayed as "4:00 PM" — a fake "future"
 * timestamp. The Delivery Intelligence audit trail was misleading
 * in operations.
 *
 * Contract
 * --------
 * - The browser owns the conversion. We never apply manual offsets.
 * - We use `Intl` / `toLocaleString` so EN, FR, ES all render in the
 *   operator's chosen language and calendar.
 * - Legacy rows that lack a tz suffix are interpreted as UTC.
 * - All helpers return `null` (not "Invalid Date" / "—") so the
 *   caller decides the fallback string for its surface.
 *
 * Coverage (replaces in-page slice patterns)
 * ------------------------------------------
 *   src/pages/UserWelcomeDispatch.jsx
 *   src/pages/CommunicationPreferences.jsx
 *   src/pages/AdminActivity.jsx
 *   src/pages/AdminRecognition.jsx
 *   src/components/campaigns/CampaignsTable.jsx
 *   src/components/campaigns/CampaignDrawer.jsx
 *
 * Regression test: src/pages/__tests__/UserWelcomeDispatch.timezone.test.js
 */

/** Normalize a stored ISO timestamp to a Date in the local zone.
 *  Treats unsuffixed strings as UTC (defensive — legacy rows).
 *  Returns null for falsy/invalid input so the caller controls the
 *  fallback. */
export const toLocalDate = (iso) => {
    if (!iso || typeof iso !== 'string') return null;
    const hasTz = /[zZ]|[+\-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(hasTz ? iso : iso + 'Z');
    return Number.isNaN(d.getTime()) ? null : d;
};

/** Short clock time in the user's locale, e.g. "12:00 PM" / "12:00". */
export const fmtLocalTime = (iso, locale) => {
    const d = toLocalDate(iso);
    if (!d) return null;
    return d.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
};

/** Compact local "YYYY-MM-DD HH:MM:SS" — matches the old visual rhythm
 *  in CampaignsTable / CampaignDrawer / AdminActivity but as the
 *  operator's LOCAL clock.
 *
 *  Seconds are always included so operators can distinguish events
 *  that fall in the same minute — critical for diagnosing duplicate
 *  tap logging. Callers that need only H:M should call `fmtLocalTime`.
 */
export const fmtLocalShort = (iso, locale) => {
    const d = toLocalDate(iso);
    if (!d) return null;
    const date = d.toLocaleDateString(locale, {
        year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const time = d.toLocaleTimeString(locale, {
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}`;
};

/** Full ISO 8601 timestamp with milliseconds preserved. Used as the
 *  tooltip / title attribute on displayed timestamps so operators can
 *  reveal sub-second precision when investigating duplicate events. */
export const fmtISOWithMillis = (iso) => {
    const d = toLocalDate(iso);
    if (!d) return null;
    return d.toISOString();
};

/** Full local date + time + timezone label. Used in drawers and audit
 *  detail views so the timezone is unambiguous. */
export const fmtLocalDateTime = (iso, locale) => {
    const d = toLocalDate(iso);
    if (!d) return null;
    return d.toLocaleString(locale, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        timeZoneName: 'short',
    });
};

export default { toLocalDate, fmtLocalTime, fmtLocalShort, fmtLocalDateTime, fmtISOWithMillis };
